import React, { useCallback, useEffect, useState } from 'react';
import { searchAPI } from '../services/api';
import { parseServerDate } from '../utils/serverDate';

// A render error here must never take the Search page down with it.
class RecentSearchesBoundary extends React.Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err) { console.error('RecentSearches failed to render:', err); }
  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

const DASH = '—';
const isNil = (v) => v === null || v === undefined || v === '' || Number.isNaN(Number(v));
const num = (v) => (isNil(v) ? DASH : Number(v).toLocaleString());
const text = (v) => (v === null || v === undefined || v === '' ? DASH : String(v));

// Soft status pill tone; unknown statuses fall back to a neutral pill.
const statusTone = (v) => {
  const s = String(v).toLowerCase();
  if (s === 'completed') return 'success';
  if (s === 'failed') return 'danger';
  if (s === 'processing' || s === 'running' || s === 'pending' || s === 'queued') return 'info';
  return 'neutral';
};
const status = (v) => (v === null || v === undefined || v === ''
  ? DASH
  : <span className={`search-status-pill is-${statusTone(v)}`}>{String(v)}</span>);

function RecentSearchesInner({ refreshKey }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await searchAPI.getHistory(10);
      const list = res?.data?.history;
      setRows(Array.isArray(list) ? list : []);
      setError(false);
    } catch (err) {
      setError(true);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  let body;
  if (error) {
    body = (
      <p className="recent-searches-note is-error">
        We couldn't load your recent searches.{' '}
        <button type="button" className="recent-searches-retry" onClick={() => { setError(false); setRows(null); load(); }}>Try again</button>
      </p>
    );
  } else if (rows === null) {
    body = <p className="recent-searches-note">Loading...</p>;
  } else if (rows.length === 0) {
    body = <p className="recent-searches-note">No searches yet.</p>;
  } else {
    body = (
      <div className="recent-searches-scroll">
        <table className="recent-searches-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Industry</th>
              <th>Location</th>
              <th>Status</th>
              <th className="num">Found</th>
              <th className="num">Added</th>
              <th className="num">Credits</th>
              <th className="num">No website</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id ?? i}>
                <td className="nowrap">{parseServerDate(r.created_at)?.toLocaleString() || DASH}</td>
                <td>{text(r.industry)}</td>
                <td>{text(r.location)}</td>
                <td>{status(r.status)}</td>
                <td className="num">{num(r.buyers_found)}</td>
                <td className="num">{num(r.buyers_added)}</td>
                <td className="num">{num(r.credits_spent)}</td>
                <td className="num">{num(r.no_website)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <section className="recent-searches">
      <h2 className="recent-searches-title">Recent searches</h2>
      {body}
    </section>
  );
}

function RecentSearches({ refreshKey = 0 }) {
  return (
    <RecentSearchesBoundary>
      <RecentSearchesInner refreshKey={refreshKey} />
    </RecentSearchesBoundary>
  );
}

export default RecentSearches;
